"use client";

import { useState } from "react";
import {
  XCircle,
  ChevronDown,
  ChevronUp,
  History,
  Sparkles,
} from "lucide-react";
import { ProblemAttempt } from "../types/problem";
import { getSortedKeys } from "../lib/problemAnswerUtils";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";

interface PreviousAnswersProps {
  /**
   * 순수 과거 시도만 (부모가 이번에 방금 제출한 답은 제외하고 넘김).
   * 정렬: 오래된 → 최신 순 (problemHistory.attempts 그대로).
   */
  attempts: ProblemAttempt[];
  /** 프리미엄 접근 여부. false면 잠금 UI. */
  canView: boolean;
  isSignedIn: boolean;
  /** 비프리미엄 잠금 상태에서 "업글" 클릭 시 호출 */
  onUpgrade: () => void;
}

type AttemptStatus = "correct" | "wrong" | "partial";

// ── 상태 판정 (ProblemHistory.tsx 와 동일 규칙: 부분정답은 !isCorrect + correctItems>0 일 때만) ──
function attemptStatus(attempt: ProblemAttempt): AttemptStatus {
  if (attempt.isCorrect) return "correct";
  if (attempt.partialScore && attempt.partialScore.correctItems > 0) {
    return "partial";
  }
  return "wrong";
}

// ── 색상 매핑 (dark-mode-colors.md §3: variant 3종 이상은 Record 매핑) ──
const BADGE_SURFACE: Record<AttemptStatus, string> = {
  correct:
    "border-green-200 bg-green-50 text-green-600 dark:border-green-800 dark:bg-green-950/40 dark:text-green-400",
  wrong:
    "border-red-200 bg-red-50 text-red-600 dark:border-red-900 dark:bg-red-950/40 dark:text-red-400",
  partial:
    "border-blue-200 bg-blue-50 text-blue-600 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-400",
};

const STATUS_LABEL: Record<AttemptStatus, string> = {
  correct: "정답",
  wrong: "오답",
  partial: "부분 정답",
};

// ── 날짜 포맷 (ProblemHistory.tsx formatAttemptTimestamp 와 동일) ──
function formatTimestamp(timestamp: number): string {
  return new Date(timestamp).toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const EMPTY_MARK = (
  <span className="text-gray-400 dark:text-gray-500">(미입력)</span>
);

/**
 * userAnswer 렌더:
 * - string → monospace, 빈 문자열은 "(미입력)". `strike` 이면 취소선.
 * - Record → 키 정렬 후 "키: 값" 나열(`① GROUP BY · ② WHERE`), 빈 값은 "(미입력)".
 */
function UserAnswerValue({
  userAnswer,
  strike = false,
}: {
  userAnswer: string | Record<string, string | number>;
  strike?: boolean;
}) {
  if (typeof userAnswer === "string") {
    if (userAnswer.trim() === "") return EMPTY_MARK;
    return (
      <span
        translate="no"
        className={cn(
          "font-mono tabular-nums break-words text-gray-900 dark:text-gray-100",
          strike &&
            "line-through decoration-red-500/70 dark:decoration-red-400/70",
        )}
      >
        {userAnswer}
      </span>
    );
  }

  const keys = getSortedKeys(userAnswer);
  if (keys.length === 0) return EMPTY_MARK;

  return (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
      {keys.map((key, index) => {
        const raw = String(userAnswer[key] ?? "");
        return (
          <span key={key} className="inline-flex items-center gap-1">
            {index > 0 && (
              <span
                className="text-gray-300 dark:text-gray-600"
                aria-hidden="true"
              >
                ·
              </span>
            )}
            <span className="text-xs font-bold text-gray-500 dark:text-gray-400">
              {key}
            </span>
            {raw.trim() === "" ? (
              EMPTY_MARK
            ) : (
              <span
                translate="no"
                className="font-mono break-words text-gray-900 dark:text-gray-100"
              >
                {raw}
              </span>
            )}
          </span>
        );
      })}
    </span>
  );
}

// ── 정오 배지: 부분정답은 "부분 N/M"(파랑), 정답 "정답"(초록), 오답 "오답"(빨강) ──
function StatusBadge({ attempt }: { attempt: ProblemAttempt }) {
  const status = attemptStatus(attempt);
  const text =
    status === "partial" && attempt.partialScore
      ? `부분 ${attempt.partialScore.correctItems}/${attempt.partialScore.totalItems}`
      : STATUS_LABEL[status];
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[11px] font-bold tabular-nums",
        BADGE_SURFACE[status],
      )}
    >
      {text}
    </span>
  );
}

// ── 잠금 UI (canView=false): ProblemHistory.tsx 업글 톤과 통일 ──
function LockedView({
  isSignedIn,
  onUpgrade,
}: {
  isSignedIn: boolean;
  onUpgrade: () => void;
}) {
  return (
    <div className="w-full text-left">
      <div className="flex w-full flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 dark:border-gray-600 dark:bg-gray-700">
        <span className="flex items-center gap-1" aria-hidden="true">
          <XCircle className="h-4 w-4 text-gray-400 dark:text-gray-500" />
          <XCircle className="h-4 w-4 text-gray-400 dark:text-gray-500" />
          <XCircle className="h-4 w-4 text-gray-400 dark:text-gray-500" />
        </span>
        <span className="text-sm text-gray-600 dark:text-gray-300">
          {isSignedIn
            ? "이전에 입력한 답안 이력은 프리미엄에서 볼 수 있어요"
            : "로그인하고 프리미엄으로 이전 답안 이력을 볼 수 있어요"}
        </span>
        <Button
          onClick={onUpgrade}
          size="sm"
          className="ml-auto bg-gradient-to-r from-yellow-400 to-orange-500 text-white hover:from-yellow-500 hover:to-orange-600 dark:from-yellow-500 dark:to-orange-600 dark:hover:from-yellow-600 dark:hover:to-orange-700"
        >
          <Sparkles className="mr-1 h-3 w-3" aria-hidden="true" />
          업글
        </Button>
      </div>
    </div>
  );
}

// 펼치기 없이 항상 바로 보여줄 최신 항목 수. 초과분(6개 이상)은 "더보기"로 이어서 편다.
const INITIAL_VISIBLE_ATTEMPTS = 5;

/**
 * 정답 공개 시 "내가 예전에 이 문제에 입력했던 답안 이력"을 보여준다.
 * 접기(<details>) 없이 정적 제목 줄 + 최신→과거 목록을 처음부터 최대 5개까지 바로 노출하고,
 * 6개 이상이면 목록 하단 "더보기"(useState 토글)로 나머지를 편다(→ "접기"로 복귀).
 * 부모(ProblemAnswer.tsx)가 <tr><td> 안에 넣으므로 자체 div 래퍼로 self-contained(내부 table 금지).
 */
export default function PreviousAnswers({
  attempts,
  canView,
  isSignedIn,
  onUpgrade,
}: PreviousAnswersProps) {
  // "더보기"(초과분 전체 표시) 토글 — hook 은 early return 앞에 위치해야 함
  const [showAllAttempts, setShowAllAttempts] = useState(false);

  // 방어적 빈 배열 처리 (부모도 attempts.length>0 로 감싸지만 이중 방어)
  if (!attempts || attempts.length === 0) return null;

  if (!canView) {
    return <LockedView isSignedIn={isSignedIn} onUpgrade={onUpgrade} />;
  }

  // 목록은 최신 → 과거 순 (attempts 는 오래된→최신이라 뒤집는다)
  const newestFirst = [...attempts].reverse();
  // 5개 초과면 최신 5개만 먼저 보여주고 나머지는 "더보기"로 이어서 표시
  const hasMoreAttempts = newestFirst.length > INITIAL_VISIBLE_ATTEMPTS;
  const hiddenAttemptCount = newestFirst.length - INITIAL_VISIBLE_ATTEMPTS;
  const visibleAttempts = showAllAttempts
    ? newestFirst
    : newestFirst.slice(0, INITIAL_VISIBLE_ATTEMPTS);

  return (
    <div className="w-full overflow-hidden rounded-lg border border-gray-200 text-left dark:border-gray-700">
      {/* 제목 줄 — 클릭/토글 없는 정적 헤더. 배지는 전체 개수 유지 */}
      <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-800 dark:bg-gray-700/70 dark:text-gray-100">
        <History
          className="h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400"
          aria-hidden="true"
        />
        <span>이전 답안 이력</span>
        <span className="rounded-full bg-gray-200/80 px-2 py-0.5 text-[11px] font-bold tabular-nums text-gray-600 dark:bg-gray-600/60 dark:text-gray-300">
          {attempts.length}회
        </span>
      </div>

      {/* 목록 — 최신→과거, 항상 최대 5개 바로 표시 (초과분은 "더보기"로 이어짐) */}
      <ul className="divide-y divide-gray-200 dark:divide-gray-700">
        {visibleAttempts.map((attempt, index) => {
          const status = attemptStatus(attempt);
          return (
            <li
              key={`${attempt.timestamp}-${index}`}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2.5"
            >
              <time
                className="shrink-0 whitespace-nowrap text-[11px] tabular-nums text-gray-500 dark:text-gray-400"
                dateTime={new Date(attempt.timestamp).toISOString()}
              >
                {formatTimestamp(attempt.timestamp)}
              </time>
              <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-1.5 gap-y-1 text-sm">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  내 답
                </span>
                <UserAnswerValue
                  userAnswer={attempt.userAnswer}
                  strike={status === "wrong"}
                />
              </span>
              <StatusBadge attempt={attempt} />
            </li>
          );
        })}
      </ul>

      {/* 더보기/접기 — 이력이 5개 초과일 때만 */}
      {hasMoreAttempts && (
        <button
          type="button"
          onClick={() => setShowAllAttempts((prev) => !prev)}
          aria-expanded={showAllAttempts}
          className="flex w-full items-center justify-center gap-1 border-t border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-amber-500 motion-reduce:transition-none dark:border-gray-700 dark:bg-gray-700/40 dark:text-gray-300 dark:hover:bg-gray-700/70 dark:hover:text-gray-100"
        >
          {showAllAttempts ? (
            <>
              <ChevronUp className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              접기
            </>
          ) : (
            <>
              <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              더보기 (+{hiddenAttemptCount})
            </>
          )}
        </button>
      )}
    </div>
  );
}
